import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import {
  DataGridPremiumProps,
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GridColDef,
  GridGroupNode,
  GridPinnedColumnFields,
  GridRowId,
  GridRowSelectionModel,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid";
import { useTenantTranslation } from "hooks/formatters";
import { Serialiser } from "hooks/serialisers/types";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { MaterialLimit } from "src/store/api/generatedApi";
import { MaterialLimitAggregateCell } from "./MaterialLimitAggregateCell";
import { MaterialLimitCell } from "./MaterialLimitCell";
import {
  ChefGroupRow,
  differsFromScrapchefDefault,
  formatSignatureLabel,
  MaterialLimitBoundField,
  MaterialLimitColumn,
  MaterialLimitScope,
} from "./materialLimitMatrix";
import { conflictsForCell, IndexedConflicts } from "./parameterConflicts";

interface Props {
  limitLabelBySignature: Record<string, string>;
  rows: ChefGroupRow[];
  columns: MaterialLimitColumn[];
  conflicts: IndexedConflicts;
  activeFieldsBySignature: Record<string, MaterialLimitBoundField[]>;
  classNameById: Record<number, string>;
  scope: MaterialLimitScope;
  hardnessView: boolean;
  basketNumberById: Record<number, number>;
  boundSerialiser: Serialiser<number | null, never>;
  editedKeys: Set<string>;
  scrapchefLimitsByKey: Record<string, MaterialLimit>;
  loading: boolean;
  rowSelectionModel: GridRowSelectionModel;
  onRowSelectionModelChange: (model: GridRowSelectionModel) => void;
  onCellChange: (
    chefGroupId: number,
    column: MaterialLimitColumn,
    field: MaterialLimitBoundField | "hardness",
    value: number | null
  ) => void;
  onCellAdd: (chefGroupId: number, column: MaterialLimitColumn) => void;
  onGroupCellChange: (
    chefGroupIds: number[],
    column: MaterialLimitColumn,
    field: MaterialLimitBoundField | "hardness",
    value: number | null
  ) => void;
}

const rowGroupingModel: DataGridPremiumProps["rowGroupingModel"] = [
  "productGroupName",
];

const pinnedColumns: GridPinnedColumnFields = {
  left: [
    GRID_CHECKBOX_SELECTION_FIELD,
    GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  ],
};

const rowSelectionPropagation: DataGridPremiumProps["rowSelectionPropagation"] =
  { parents: true, descendants: true };

const getRowClassName = (params: { id: GridRowId }): string =>
  typeof params.id === "string" && params.id.startsWith("auto-generated")
    ? "product-group-row"
    : "";

const HARDNESS_COLUMN_WIDTH = 104;
const columnWidth = (fieldCount: number): number =>
  Math.max(108, 52 + fieldCount * 70);

export const MaterialLimitsGrid = ({
  limitLabelBySignature,
  rows,
  columns,
  conflicts,
  activeFieldsBySignature,
  classNameById,
  scope,
  hardnessView,
  basketNumberById,
  boundSerialiser,
  editedKeys,
  scrapchefLimitsByKey,
  loading,
  rowSelectionModel,
  onRowSelectionModelChange,
  onCellChange,
  onCellAdd,
  onGroupCellChange,
}: Props) => {
  const theme = useTheme();
  const { t } = useTenantTranslation();
  const apiRef = useGridApiRef();
  const expansionStateRef = useRef(new Map<GridRowId, boolean>());

  const isGroupExpandedByDefault = useCallback(
    (node: GridGroupNode) => expansionStateRef.current.get(node.id) ?? false,
    []
  );

  useEffect(() => {
    if (!apiRef.current) return;
    return apiRef.current.subscribeEvent("rowExpansionChange", (node) => {
      expansionStateRef.current.set(node.id, node.childrenExpanded ?? false);
    });
  }, [apiRef]);

  const childLimitsFor = useCallback(
    (node: GridGroupNode, signature: string): MaterialLimit[] =>
      node.children
        .map((id) => apiRef.current?.getRow<ChefGroupRow>(id))
        .filter((row): row is ChefGroupRow => row != null)
        .map((row) => row.limitsBySignature[signature])
        .filter((limit): limit is MaterialLimit => limit != null),
    [apiRef]
  );

  const groupDiffersFromScrapchef = useCallback(
    (node: GridGroupNode, signature: string): boolean =>
      node.children.some(
        (id) =>
          typeof id === "number" &&
          differsFromScrapchefDefault(
            apiRef.current?.getRow<ChefGroupRow>(id)?.limitsBySignature[
              signature
            ],
            scrapchefLimitsByKey[`${id}::${signature}`]
          )
      ),
    [apiRef, scrapchefLimitsByKey]
  );

  const gridColumns = useMemo<GridColDef<ChefGroupRow>[]>(
    () => [
      {
        field: "productGroupName",
        headerName: t("productGroup"),
        sortable: false,
      },
      {
        field: "chefGroupName",
        headerName: t("chemistryGroup"),
        sortable: false,
        renderCell: (params) =>
          params.rowNode.type === "group" ? null : (
            <Tooltip
              placement="right"
              title={
                <Box>
                  <Typography variant="subtitle2">
                    {params.row.chefGroupName}
                  </Typography>
                  {params.row.steelGradeNames.length > 0 ? (
                    <Typography variant="caption">
                      {params.row.steelGradeNames.join(", ")}
                    </Typography>
                  ) : null}
                </Box>
              }
            >
              <Box
                sx={{
                  minWidth: 0,
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {params.row.chefGroupName}
              </Box>
            </Tooltip>
          ),
      },
      ...columns.map((column, index): GridColDef<ChefGroupRow> => {
        const activeFields = activeFieldsBySignature[column.signature] ?? [];
        const signatureLabel = formatSignatureLabel({
          coefficients: column.coefficients,
          classNameById,
          translate: t,
        });
        const headerLabel = column.isExplicitlyNamed
          ? t(column.name)
          : signatureLabel;
        return {
          field: `limit_${index}`,
          headerName: headerLabel,
          cellClassName: (params) => {
            const edited =
              params.rowNode.type === "group"
                ? params.rowNode.children.some(
                    (id) =>
                      typeof id === "number" &&
                      editedKeys.has(`${id}::${column.signature}`)
                  )
                : editedKeys.has(`${params.id}::${column.signature}`);
            return edited ? "limit-cell edited-cell" : "limit-cell";
          },
          width: hardnessView
            ? HARDNESS_COLUMN_WIDTH
            : columnWidth(activeFields.length),
          sortable: false,
          renderHeader: () => (
            <Tooltip
              placement="top"
              title={
                <Box>
                  <Typography variant="subtitle2">{headerLabel}</Typography>
                  {column.isExplicitlyNamed ? (
                    <Typography variant="caption">{signatureLabel}</Typography>
                  ) : null}
                </Box>
              }
            >
              <Box
                sx={{
                  typography: "body2",
                  color: "text.secondary",
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {headerLabel}
              </Box>
            </Tooltip>
          ),
          renderCell: (params) => {
            if (params.rowNode.type === "group") {
              const childChefGroupIds = params.rowNode.children.filter(
                (id): id is number => typeof id === "number"
              );
              return (
                <MaterialLimitAggregateCell
                  limits={childLimitsFor(params.rowNode, column.signature)}
                  childCount={childChefGroupIds.length}
                  scope={scope}
                  hardnessView={hardnessView}
                  activeFields={activeFields}
                  boundSerialiser={boundSerialiser}
                  conflicts={childChefGroupIds.flatMap((id) =>
                    conflictsForCell(
                      conflicts,
                      id,
                      column.coefficientsSignature
                    )
                  )}
                  anyChildDiffersFromScrapchef={groupDiffersFromScrapchef(
                    params.rowNode,
                    column.signature
                  )}
                  onChange={(field, value) => {
                    onGroupCellChange(childChefGroupIds, column, field, value);
                  }}
                />
              );
            }
            return (
              <MaterialLimitCell
                limit={params.row.limitsBySignature[column.signature]}
                scrapchefLimit={
                  scrapchefLimitsByKey[`${params.row.id}::${column.signature}`]
                }
                conflicts={conflictsForCell(
                  conflicts,
                  params.row.id,
                  column.coefficientsSignature
                )}
                limitLabel={
                  limitLabelBySignature[column.coefficientsSignature] ??
                  column.coefficientsSignature
                }
                activeFields={activeFields}
                scope={scope}
                hardnessView={hardnessView}
                basketNumberById={basketNumberById}
                boundSerialiser={boundSerialiser}
                onChangeField={(field, value) => {
                  onCellChange(params.row.id, column, field, value);
                }}
                onAdd={() => {
                  onCellAdd(params.row.id, column);
                }}
              />
            );
          },
        };
      }),
    ],
    [
      columns,
      limitLabelBySignature,
      conflicts,
      activeFieldsBySignature,
      classNameById,
      scope,
      hardnessView,
      basketNumberById,
      boundSerialiser,
      editedKeys,
      scrapchefLimitsByKey,
      childLimitsFor,
      groupDiffersFromScrapchef,
      onCellAdd,
      onCellChange,
      onGroupCellChange,
      t,
    ]
  );

  const groupingColDef: DataGridPremiumProps<ChefGroupRow>["groupingColDef"] =
    useMemo(
      () => ({
        headerName: t("group"),
        leafField: "chefGroupName",
        width: 260,
        hideDescendantCount: true,
      }),
      [t]
    );

  const initialState: DataGridPremiumProps["initialState"] = useMemo(
    () => ({
      columns: {
        columnVisibilityModel: {
          productGroupName: false,
          chefGroupName: false,
        },
      },
    }),
    []
  );

  return (
    <HotkeyCompliantDataGrid<ChefGroupRow>
      apiRef={apiRef}
      rows={rows}
      columns={gridColumns}
      getRowId={(row) => row.id}
      loading={loading}
      rowHeight={44}
      checkboxSelection
      disableRowSelectionOnClick
      keepNonExistentRowsSelected
      showCellVerticalBorder
      showColumnVerticalBorder
      rowSelectionModel={rowSelectionModel}
      onRowSelectionModelChange={onRowSelectionModelChange}
      rowSelectionPropagation={rowSelectionPropagation}
      rowGroupingModel={rowGroupingModel}
      groupingColDef={groupingColDef}
      isGroupExpandedByDefault={isGroupExpandedByDefault}
      getRowClassName={getRowClassName}
      pinnedColumns={pinnedColumns}
      initialState={initialState}
      hideFooter
      disableColumnMenu
      sx={{
        flex: 1,
        minHeight: 0,
        "& .product-group-row": {
          backgroundColor: theme.palette.action.hover,
          fontWeight: "fontWeightBold",
        },
        "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
        "& .MuiDataGrid-columnHeaderTitleContainerContent": {
          flex: 1,
          minWidth: 0,
        },
        "& .limit-cell": { overflow: "visible", position: "relative" },
        "& .limit-cell:hover": { zIndex: 5 },
        "& .edited-cell": { bgcolor: theme.palette.warning.light },
      }}
    />
  );
};
